import type { OrderListItem } from '@agos/api-client/model'
import { T, cityFromAddress, formatDayMonth, formatTime, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import {
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  ConciergeBell,
  MapPin,
  Package,
  PlayCircle,
  ShoppingBag,
  Store,
  Truck,
} from 'lucide-react'

import { KitchenCallButton } from '../kitchen-calls/KitchenCallButton'
import {
  ELAPSED_BG_COLORS,
  ELAPSED_TEXT_COLORS,
  elapsedBand,
  elapsedFormatted,
  formatCountdown,
  minutesUntil,
  type ItemProgress,
} from './elapsed'
import { KITCHEN_TEXT } from './item-name'
import { ON_SOLID } from './kitchen-theme'
import { KitchenItemCard, type KitchenDescriptions } from './KitchenItemCard'
import { groupOrderItems, type KitchenLine } from './kitchen-items'
import { itemKey, kitchenRounds, shownItemCount } from './kitchen-rounds'

/**
 * One ticket on the kitchen display.
 *
 * Everything about it is sized for a tablet on a wall being read from a metre
 * away: the order number is the largest thing on the card, the channel is a
 * colour strip rather than a word, and the elapsed-time bar changes colour
 * before it changes text. §14.9 — this runs on the restaurant's tablets, and a
 * card that only works at desktop width fails where the food is made.
 */

const TYPE_STRIP_COLORS: Record<string, string> = {
  delivery: 'bg-blue-600',
  restaurant: 'bg-red-600',
  bar: 'bg-emerald-600',
}

const TYPE_SHORT: Record<string, string> = {
  delivery: 'ROZ',
  restaurant: 'RES',
  bar: 'OSB',
}

const TYPE_ICONS: Record<string, typeof Store> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

/** Inside this many minutes the scheduled countdown turns red. */
const IMMINENT_MINUTES = 15

export interface KitchenOrderCardProps {
  order: OrderListItem
  lines: KitchenLine[]
  descriptions: KitchenDescriptions
  now: number
  preorderThresholdMinutes: number
  /** Held on screen for a moment after it was handed over. */
  isCompleting: boolean
  isBusy: boolean
  canAccept: boolean
  /**
   * A line's tap - §9.811, owner O157. The key is the card's own (`itemKey`, round and position); the line is what
   * the board reads the server's mark off and sends a tap for, by its row id.
   */
  progressOf: (key: string, line: KitchenLine) => ItemProgress
  onToggleItem: (key: string, line: KitchenLine) => void
  onAdvance: (order: OrderListItem) => void
  /**
   * §9.720, owner O95 - whether this ticket carries the bell that calls a waiter. The board decides
   * (`kitchenCallOffered`: an on-premise order only), so a delivery's ticket has none.
   */
  canCallWaiter?: boolean
}

export function KitchenOrderCard({
  order,
  lines,
  descriptions,
  now,
  preorderThresholdMinutes,
  isCompleting,
  isBusy,
  canAccept,
  progressOf,
  onToggleItem,
  onAdvance,
  canCallWaiter = false,
}: KitchenOrderCardProps) {
  const isNew = order.status === 'new' && !isCompleting
  const stripColor = TYPE_STRIP_COLORS[order.type ?? ''] ?? TYPE_STRIP_COLORS.restaurant
  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Store
  // Doobjednávky: a table that ordered again after the kitchen had finished. The rounds render
  // newest-last; an earlier one fades, and folds into one line once the waiter carried all of it out
  // - §9.670, owner #20. The count is of what is drawn, so it says how much is left to read.
  const rounds = kitchenRounds(lines)
  const latestRound = rounds[rounds.length - 1]?.round ?? 1
  const itemCount = shownItemCount(rounds)
  const band = elapsedBand(order.createdAt ?? '', now)

  const minutesToSchedule = order.scheduledAt ? minutesUntil(order.scheduledAt, now) : null
  const isDueSoon =
    order.isPreorder === true &&
    minutesToSchedule !== null &&
    minutesToSchedule <= preorderThresholdMinutes &&
    minutesToSchedule > 0

  return (
    <div
      /* §9.599 - a hook per ticket, so a spec can assert that *this* card offers the button for *its own*
         status. Counting the two buttons over the whole board cannot: on a two-ticket board one of each
         makes the counts equal, and swapping the pairing passed. */
      data-kitchen-ticket={order.id}
      className={cn(
        'w-full flex flex-col rounded-lg overflow-hidden transition-all duration-500',
        'bg-white dark:bg-[#161622] border border-slate-200 dark:border-white/8',
        isCompleting && 'opacity-40 scale-[0.97]',
      )}
    >
      <div className={cn('px-3 py-1.5 flex items-center justify-between', stripColor)}>
        <div className="flex items-center gap-1.5">
          <TypeIcon className={cn('w-3.5 h-3.5', ON_SOLID.soft)} />
          <span className={cn('text-[11px] font-black tracking-widest', ON_SOLID.text)}>
            {TYPE_SHORT[order.type ?? ''] ?? (order.type ?? '').toUpperCase().slice(0, 3)}
          </span>
        </div>
        <span className={cn(KITCHEN_TEXT.label, 'font-bold', ON_SOLID.muted)}>
          {itemCount} {tt('pol.')}
        </span>
      </div>

      <div className="px-3 pt-2.5 pb-1 bg-slate-50 dark:bg-[#12121e]">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1',
                isCompleting ? 'bg-emerald-400' : isNew ? 'bg-red-400 animate-pulse' : 'bg-blue-400',
              )}
            />
            <span className="font-mono text-3xl font-black text-slate-900 dark:text-white leading-none tracking-tight">
              #{order.orderNumber?.split('-').pop()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-slate-400 dark:text-white/30 font-medium">
              {order.createdAt ? formatTime(order.createdAt) : ''}
            </p>
            {/* §9.720 - the soup goes out before the main course and changes no status, so the kitchen calls. */}
            {canCallWaiter && order.id !== undefined && <KitchenCallButton orderId={order.id} />}
          </div>
        </div>
      </div>

      {/* §9.810, owner O156 - *"časenka ... výraznejšie, väčšie"*: the time is the largest thing after the number. */}
      <div
        data-kitchen-elapsed
        className={cn(
          'mx-3 mt-1 mb-1.5 rounded-md flex items-center justify-center gap-2 py-2',
          ELAPSED_BG_COLORS[band],
        )}
      >
        <Clock className={cn('w-5 h-5', ELAPSED_TEXT_COLORS[band])} />
        <span className={cn(KITCHEN_TEXT.elapsed, 'font-black tabular-nums leading-none', ELAPSED_TEXT_COLORS[band])}>
          {elapsedFormatted(order.createdAt ?? '', now)}
        </span>
        {isDueSoon && minutesToSchedule !== null && (
          <>
            <span className="text-slate-300 dark:text-white/15 mx-1">|</span>
            <Bell className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            <span className={cn(KITCHEN_TEXT.countdown, 'font-black text-amber-700 dark:text-amber-400')}>
              {formatCountdown(minutesToSchedule)}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-col gap-1.5 px-0">
        {order.isPreorder === true && order.scheduledAt && minutesToSchedule !== null && (
          <div
            data-kitchen-scheduled
            className={cn(
              KITCHEN_TEXT.countdown,
              'mx-3 flex items-center gap-1.5 bg-amber-500/12 border border-amber-500/20 rounded px-2 py-1.5 text-amber-700 dark:text-amber-400',
            )}
          >
            <CalendarClock className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>
                {formatDayMonth(order.scheduledAt)} {formatTime(order.scheduledAt)}
              </strong>
              {' · '}
              <span
                className={cn('font-bold', minutesToSchedule <= IMMINENT_MINUTES && 'text-red-700 dark:text-red-400')}
              >
                {formatCountdown(minutesToSchedule)}
              </span>
            </span>
          </div>
        )}

        {order.type === 'delivery' && (order.customerName || order.customerAddress) && (
          <div className="mx-3 space-y-0.5 bg-slate-900/3 dark:bg-white/3 rounded px-2 py-1.5">
            {order.customerName && (
              <p className={cn(KITCHEN_TEXT.note, 'text-slate-500 dark:text-white/40 leading-tight')}>{order.customerName}</p>
            )}
            {order.customerAddress && (
              <div className="flex items-start gap-1">
                <MapPin className="w-3 h-3 text-slate-500 dark:text-white/40 shrink-0 mt-0.5" />
                {/* The town, not the street: the cook wants to know which
                    village the driver is going to, not the house number. */}
                <p className={cn(KITCHEN_TEXT.note, 'font-black text-slate-700 dark:text-white/70 leading-tight')}>
                  {cityFromAddress(order.customerAddress)}
                </p>
              </div>
            )}
          </div>
        )}

        {(order.employeeName ?? order.driverName) && (
          <div
            className={cn(
              KITCHEN_TEXT.label,
              'mx-3 flex items-center gap-1 text-violet-700 dark:text-violet-300 bg-violet-500/12 rounded px-2 py-1 w-fit',
            )}
          >
            <ConciergeBell className="w-3 h-3" />
            {order.employeeName ?? order.driverName}
          </div>
        )}

        {/* §9.810, owner O156 - *"poznámka ... výraznejšie, väčšie"*: a solid block in large type, read from the pass. */}
        {order.notes && (
          <div
            data-kitchen-order-note
            className={cn(
              KITCHEN_TEXT.orderNote,
              'mx-3 mb-1 rounded-md px-2.5 py-2 font-bold leading-snug break-words',
              'bg-amber-100 text-amber-950 border-2 border-amber-400 dark:bg-amber-500/25 dark:text-amber-100 dark:border-amber-400/60',
            )}
          >
            📝 {order.notes}
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-white/5">
          {rounds.map(({ round, lines: roundLines, earlier: isEarlierRound, folded }) => {
            if (folded) {
              // §9.670 - a round that is on the table is one line, not a list a cook reads past.
              return (
                <div
                  key={round}
                  data-kitchen-round-served={round}
                  className="flex items-center gap-1.5 px-2.5 py-1 border-b bg-slate-900/3 dark:bg-white/3 border-slate-200 dark:border-white/5"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-emerald-700/60 dark:text-emerald-400/60" />
                  <span
                    className={cn(KITCHEN_TEXT.label, 'font-black uppercase tracking-widest text-slate-500 dark:text-white/40')}
                  >
                    {`${tt('Kolo')} ${round} - ${tt('vynesené')}`}
                  </span>
                </div>
              )
            }
            return (
              <div key={round} className={cn(isEarlierRound && 'opacity-35')}>
                {latestRound > 1 && (
                  <div
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-0.5 border-b',
                      isEarlierRound
                        ? 'bg-slate-900/3 dark:bg-white/3 border-slate-200 dark:border-white/5'
                        : 'bg-orange-500/12 border-orange-500/15',
                    )}
                  >
                    <span
                      className={cn(
                        KITCHEN_TEXT.label,
                        'font-black uppercase tracking-widest',
                        isEarlierRound ? 'text-slate-400 dark:text-white/20' : 'text-orange-700 dark:text-orange-400',
                      )}
                    >
                      {isEarlierRound
                        ? `${tt('Kolo')} ${round}`
                        : `${tt('Doobjednávka (kolo')} ${round})`}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1.5 px-2 py-1.5">
                  {groupOrderItems(roundLines).map((entry, entryIndex) =>
                    entry.type === 'box' ? (
                      <div
                        key={entry.groupId}
                        data-kitchen-box={entry.groupId}
                        className="flex flex-col rounded-lg border border-slate-200 dark:border-white/8 overflow-hidden bg-slate-900/3 dark:bg-white/3"
                      >
                        <div className="flex items-center gap-1 px-2.5 py-1 bg-slate-900/4 dark:bg-white/4">
                          <Package className="w-3 h-3 text-slate-400 dark:text-white/35 flex-shrink-0" />
                          <span
                            className={cn(
                              KITCHEN_TEXT.label,
                              'font-black text-slate-600 dark:text-white/60 uppercase tracking-wide',
                            )}
                          >
                            {entry.groupName}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 p-1.5">
                          {entry.lines.map((line, slotIndex) => {
                            const key = itemKey(order.id ?? 0, round, entryIndex, slotIndex)
                            return (
                              <KitchenItemCard
                                key={key}
                                line={line}
                                descriptions={descriptions}
                                progress={progressOf(key, line)}
                                onToggle={() => onToggleItem(key, line)}
                              />
                            )
                          })}
                        </div>
                      </div>
                    ) : entry.addons === undefined ? (
                      <KitchenItemCard
                        key={itemKey(order.id ?? 0, round, entryIndex)}
                        line={entry.line}
                        descriptions={descriptions}
                        progress={progressOf(itemKey(order.id ?? 0, round, entryIndex), entry.line)}
                        onToggle={() => onToggleItem(itemKey(order.id ?? 0, round, entryIndex), entry.line)}
                      />
                    ) : (
                      /* §9.713 - *"+ Hranolky"* under the burger it was ordered with, so the cook packs them
                         together. Each add-on keeps its own tap: a single entry has no slots, so the slot index of
                         `itemKey` is free for them and no key collides with the parent's. */
                      <div
                        key={itemKey(order.id ?? 0, round, entryIndex)}
                        data-kitchen-line-family={entry.line.id ?? ''}
                        className="flex flex-col gap-1"
                      >
                        <KitchenItemCard
                          line={entry.line}
                          descriptions={descriptions}
                          progress={progressOf(itemKey(order.id ?? 0, round, entryIndex), entry.line)}
                          onToggle={() => onToggleItem(itemKey(order.id ?? 0, round, entryIndex), entry.line)}
                        />
                        <div
                          data-kitchen-addons={entry.line.id ?? ''}
                          className="flex flex-col gap-1 ml-4 pl-2 border-l-2 border-orange-500/30"
                        >
                          {entry.addons.map((addon, addonIndex) => {
                            const key = itemKey(order.id ?? 0, round, entryIndex, addonIndex)
                            return (
                              <KitchenItemCard
                                key={key}
                                line={addon}
                                descriptions={descriptions}
                                progress={progressOf(key, addon)}
                                onToggle={() => onToggleItem(key, addon)}
                                addon
                              />
                            )
                          })}
                        </div>
                      </div>
                    ),
                  )}
                  {roundLines.length === 0 && (
                    <p className="text-[11px] text-slate-400 dark:text-white/20 px-2.5 py-1.5">
                      <T>Žiadne položky</T>
                    </p>
                  )}
                </div>
              </div>
            )
          })}
          {lines.length === 0 && (
            <p className="text-[11px] text-slate-400 dark:text-white/20 px-2.5 py-1.5">
              <T>Žiadne položky</T>
            </p>
          )}
        </div>
      </div>

      <div className="p-2.5">
        {isCompleting ? (
          <div className="w-full py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center gap-2 text-[15px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
            <CheckCircle2 className="w-5 h-5" /> <T>Odovzdaná</T>
          </div>
        ) : isNew && !canAccept ? (
          <div className="w-full py-3 rounded-lg bg-slate-900/5 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center gap-2 text-[13px] font-bold text-slate-400 dark:text-white/30 uppercase tracking-wider">
            <PlayCircle className="w-4 h-4" /> <T>Bez oprávnenia</T>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAdvance(order)}
            disabled={isBusy}
            className={cn(
              'w-full py-3 rounded-lg font-black text-[15px] uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-50 active:scale-95',
              isNew
                ? 'bg-green-500 hover:bg-green-400 shadow-lg shadow-green-500/30'
                : 'bg-rose-500 hover:bg-rose-400 shadow-lg shadow-rose-500/30',
              ON_SOLID.text,
            )}
          >
            {isNew ? (
              <>
                <PlayCircle className="w-5 h-5" /> <T>Prijatá</T>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" /> <T>Hotová</T>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
